import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaEdit, FaEye } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import { fetchCountries, fetchGovernments, fetchCenters, clearError, clearSuccess } from '../../../redux/Slices/LocationsSlice';

const Centers = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { countries, governments, centers, loading, error, success } = useSelector((state) => state.locations);
    const [selectedCountryId, setSelectedCountryId] = useState('');
    const [selectedGovernmentId, setSelectedGovernmentId] = useState('');

    useEffect(() => {
        setTitle('المراكز');
        dispatch(fetchCountries());
    }, [setTitle, dispatch]);

    // Fetch governments when country selected
    useEffect(() => {
        if (selectedCountryId) {
            dispatch(fetchGovernments(selectedCountryId));
            setSelectedGovernmentId(''); // Reset government when country changes
        }
    }, [selectedCountryId, dispatch]);

    // Fetch centers when government selected
    useEffect(() => {
        if (selectedGovernmentId) {
            dispatch(fetchCenters(selectedGovernmentId));
        }
    }, [selectedGovernmentId, dispatch]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
        if (success) {
            showSuccessToast(success);
            dispatch(clearSuccess());
        }
    }, [error, success, dispatch]);

    const handleEdit = (id) => navigate(`/admin-locations/centers/${id}/edit`);
    const handleView = (id) => navigate(`/admin-locations/centers/${id}`);
    const handleAdd = () => navigate(`/admin-locations/centers/add`);

    return (
        <div className="main_content">
            <div className="dashboard-container">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة المراكز</h4>
                    <button className="btn btn-primary px-4 py-2" onClick={handleAdd}>
                        إضافة مركز +
                    </button>
                </div>

                <div className="card shadow-lg border-0 mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-bold">الدولة:</label>
                                <select 
                                    className="form-select"
                                    value={selectedCountryId}
                                    onChange={(e) => setSelectedCountryId(e.target.value)}
                                >
                                    <option value="" disabled>-- اختر الدولة --</option>
                                    {countries && countries.map(country => (
                                        <option key={country.id} value={country.id}>
                                            {country.name_ar || country.name || country.name_en}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-bold">المحافظة:</label>
                                <select 
                                    className="form-select"
                                    value={selectedGovernmentId}
                                    onChange={(e) => setSelectedGovernmentId(e.target.value)}
                                    disabled={!selectedCountryId || governments.length === 0}
                                >
                                    <option value="" disabled>-- اختر المحافظة --</option>
                                    {governments && governments.map(gov => (
                                        <option key={gov.id} value={gov.id}>
                                            {gov.name_ar || gov.name || gov.name_en}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {selectedGovernmentId && (
                    <div className="card shadow-lg border-0">
                        <div className="card-body">
                            {loading && (!centers || centers.length === 0) ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">جاري تحميل المراكز...</p>
                                </div>
                            ) : !centers || centers.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="text-muted">لا توجد مراكز مضافة لهذه المحافظة</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            <tr>
                                                <th className="text-center">الرقم التعريفي</th>
                                                <th className="text-center">اسم المركز</th>
                                                <th className="text-center">الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {centers.map((center) => (
                                                <tr key={center.id} className="table-row-modern">
                                                    <td className="text-center">
                                                        <div className="order-number-badge">{center.id}</div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="category-name">
                                                            {center.name_ar || center.name_en || center.name || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="action-buttons">
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-success"
                                                                style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}
                                                                onClick={() => handleView(center.id)}
                                                                title="عرض">
                                                                <FaEye color="white" />
                                                            </motion.button>
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-warning"
                                                                style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}
                                                                onClick={() => handleEdit(center.id)}
                                                                title="تعديل">
                                                                <FaEdit color="white" />
                                                            </motion.button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Centers;
